// 会话：登录、刷新（跨标签互斥）、登出。access 过期前 30 s 自动换新。
// "管理员待绑定 TOTP"（mfa_pending）、"管理员设的密码待改"（pw_change）从访问令牌里读：刷新页面、其他标签同步都不会丢。
// refresh 在 httpOnly cookie 里：这里的请求带 X-PB-Refresh-Transport 头，服务端才读 cookie、才不把 refresh 放进响应体。

import { create } from "zustand";
import { ApiError } from "./api";
import { toast } from "./toast";
import { zh } from "@/i18n/zh";
import type { LoginResponse, TokenPair } from "./types";
import {
  loadTokens,
  onTokensChangedElsewhere,
  REFRESH_SKEW_MS,
  saveTokens,
  withRefreshLock,
  type Tokens,
} from "./tokens";

/** 读 JWT 载荷里的限制标记（只用于界面引导；服务端仍逐次判定）。 */
function claimFlag(access: string | undefined, name: "mfa_pending" | "pw_change"): boolean {
  const part = access?.split(".")[1];
  if (!part) return false;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return (JSON.parse(json) as Record<string, unknown>)[name] === true;
  } catch {
    return false;
  }
}

export const mfaPending = (access: string | undefined) => claimFlag(access, "mfa_pending");
export const passwordChangePending = (access: string | undefined) => claimFlag(access, "pw_change");

type SessionState = {
  authed: boolean;
  /** 管理员角色未绑定 TOTP（且服务端强制）：除 TOTP/me/登出外都会 403 */
  needsTotpSetup: boolean;
  /** 密码是管理员设的、还没改：除改密/TOTP/me/登出外都会 403 */
  needsPasswordChange: boolean;
};

const initial = loadTokens();

export const useSession = create<SessionState>(() => ({
  authed: initial !== null,
  needsTotpSetup: mfaPending(initial?.access),
  needsPasswordChange: passwordChangePending(initial?.access),
}));

let tokens: Tokens | null = initial;
let inflight: Promise<string | null> | null = null;

function adopt(t: Tokens | null) {
  tokens = t;
  const next = {
    authed: t !== null,
    needsTotpSetup: mfaPending(t?.access),
    needsPasswordChange: passwordChangePending(t?.access),
  };
  const cur = useSession.getState();
  if (cur.authed !== next.authed || cur.needsTotpSetup !== next.needsTotpSetup || cur.needsPasswordChange !== next.needsPasswordChange) {
    useSession.setState(next);
  }
}

onTokensChangedElsewhere(adopt);

function fromPair(p: TokenPair): Tokens {
  return { access: p.access, expiresAt: Date.now() + p.expires_in * 1000 };
}

async function post<T>(path: string, body: unknown, bearer?: string): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-PB-Refresh-Transport": "cookie",
  };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  const r = await fetch(path, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(15_000),
  });
  if (!r.ok) {
    let code = "http_error";
    let message = r.statusText;
    let details: unknown;
    try {
      const b = await r.json();
      code = b.code ?? code;
      message = b.message ?? message;
      details = b.details;
    } catch {
      /* 非 JSON 错误体 */
    }
    throw new ApiError(r.status, code, message, details);
  }
  return (await r.json()) as T;
}

/** 锁内：用 cookie 里的 refresh 换新（调用方已持锁）。返回新令牌；refresh 失效返回 null。 */
async function rotate(): Promise<Tokens | null> {
  try {
    const t = fromPair(await post<TokenPair>("/v1/auth/refresh", {}));
    // 刷新途中别处登出了：不要把会话写回去
    if (!loadTokens()) return null;
    saveTokens(t);
    return t;
  } catch (e) {
    // 401：refresh 失效/被吊销 → 登出；网络错误等：保留会话，让调用方报错
    if (e instanceof ApiError && (e.status === 401 || e.status === 400)) {
      saveTokens(null);
      return null;
    }
    throw e;
  }
}

function doRefresh(): Promise<string | null> {
  inflight ??= withRefreshLock(async () => {
    const stored = loadTokens();
    if (!stored) {
      adopt(null);
      return null;
    }
    // 别的标签已经刷新过：直接用它的
    if (stored.access !== tokens?.access && stored.expiresAt - Date.now() > REFRESH_SKEW_MS) {
      adopt(stored);
      return stored.access;
    }
    const t = await rotate();
    adopt(t);
    return t?.access ?? null;
  }).finally(() => {
    inflight = null;
  });
  return inflight;
}

export const auth = {
  async login(req: { email?: string; phone?: string; password: string; totp?: string; recovery_code?: string }): Promise<LoginResponse> {
    const r = await post<LoginResponse>("/v1/auth/login", req);
    const t = fromPair(r);
    saveTokens(t);
    adopt(t);
    return r;
  },

  /** 当前可用的 access；快过期则先刷新。未登录返回 null。 */
  async accessToken(): Promise<string | null> {
    if (!tokens) return null;
    if (tokens.expiresAt - Date.now() > REFRESH_SKEW_MS) return tokens.access;
    return doRefresh();
  },

  /** 服务端拒绝了 `failed`（401）：除非已被别处换新，否则刷新。 */
  async forceRefresh(failed: string | null): Promise<string | null> {
    if (tokens && tokens.access !== failed) return tokens.access;
    return doRefresh();
  },

  /** TOTP 绑定完成后换掉带 mfa_pending 的令牌。 */
  async refreshNow(): Promise<string | null> {
    const cur = tokens?.access ?? null;
    return this.forceRefresh(cur);
  },

  /**
   * 改自己的密码。服务端吊销全部 refresh 并返回新令牌：在刷新锁内换掉，
   * 否则别的标签正好拿旧 refresh 去刷新会得到 401，把刚换好的会话清掉。
   */
  async changePassword(current: string, next: string): Promise<void> {
    const access = (await this.accessToken()) ?? undefined;
    await withRefreshLock(async () => {
      // 等锁期间别处登出了：不要把会话写回去（与 rotate 相同）
      if (!loadTokens()) {
        adopt(null);
        return;
      }
      const t = fromPair(await post<TokenPair>("/v1/auth/password", { current, new: next }, access));
      saveTokens(t);
      adopt(t);
    });
  },

  /** 服务端说"先绑定 TOTP"（403 auth.totp_setup_required）：令牌里没带标记时也引导过去。 */
  totpSetupRequired() {
    if (!useSession.getState().needsTotpSetup) useSession.setState({ needsTotpSetup: true });
  },

  /** 同上：密码必须先改。 */
  passwordChangeRequired() {
    if (!useSession.getState().needsPasswordChange) useSession.setState({ needsPasswordChange: true });
  },

  expire() {
    saveTokens(null);
    adopt(null);
  },

  /**
   * 登出：与刷新互斥（否则别的标签刚换到的新令牌会把会话带回来）。access 过期也能登出（服务端凭 cookie 吊销）；
   * "所有设备"要有效的 access，过期时先换新。服务端没收到的话 httpOnly cookie 还留着，页面清不掉，要告诉用户。
   */
  async logout(all = false) {
    await withRefreshLock(async () => {
      let t = loadTokens();
      if (all && t && t.expiresAt - Date.now() <= REFRESH_SKEW_MS) {
        try {
          t = await rotate();
        } catch {
          /* 离线：本地照样清掉 */
        }
      }
      // 普通登出不靠本地令牌：localStorage 不可用时会话只在内存里，读不到也要让服务端吊销 cookie
      if (t || !all) {
        try {
          await post(all ? "/v1/auth/logout?all=true" : "/v1/auth/logout", {}, t?.access ?? tokens?.access);
        } catch {
          // 本地照样清掉，但服务端的会话和 cookie 还在
          toast(zh.errors.logoutIncomplete, "error");
        }
      }
      saveTokens(null);
    });
    adopt(null);
  },
};
